import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FeatureCollection } from 'geojson';
import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { useActiveApiRequestCount } from '@/hooks/useActiveApiRequestCount';
import { MapActivityBorder } from './MapActivityBorder';
import Map, { AttributionControl, type MapRef } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { cn } from 'shared-ui';
import { useExclusivePanel } from '@/panels/useExclusivePanel';
import { useMapActions } from '@/map-actions/useMapActions';
import { useMapInteraction } from '@/map-interaction/useMapInteraction';
import { useMapInteractionCommands } from '@/map-interaction/useMapInteractionCommands';
import { toast } from 'sonner';
import { useStatsScope } from '@/stats/useStatsScope';
import { useStatsBarSlot } from '@/stats/StatsBarSlotContext';
import { useStatsScopeResolver } from '@/hooks/useStatsScopeResolver';
import type { StatsScope } from '@/components/map/utils/resolveStatsScope';
import { useAppConfig } from '@/config/AppConfigContext';
import { showErrorToast } from '@/errors/showErrorToast';
import { showMapLoadErrorToast } from '@/errors/showMapLoadErrorToast';
import { useClient } from '@/clients/useClient';
import { useGlobeMode } from '@/hooks/useGlobeMode';
import { useMapLayerVisibility } from '@/hooks/useMapLayerVisibility';
import { useDistrictMeterAreasLayer } from '@/hooks/useDistrictMeterAreasLayer';
import { useDmaInteractions } from '@/hooks/useDmaInteractions';
import { useAreaInteractions } from '@/hooks/useAreaInteractions';
import { useFeatureSelection } from '@/hooks/useFeatureSelection';
import { useSelectionStats, type MapSelection } from '@/hooks/useSelectionStats';
import { useSurveyedAreasLayer } from '@/hooks/useSurveyedAreasLayer';
import { usePipelinesLayer } from '@/hooks/usePipelinesLayer';
import { usePipelineInteractions } from '@/hooks/usePipelineInteractions';
import { useFittingInteractions } from '@/hooks/useFittingInteractions';
import { useGridTilesLayer } from '@/hooks/useGridTilesLayer';
import { useFittingsLayer } from '@/hooks/useFittingsLayer';
import { useEntitiesLayer, type EntityFeeds } from '@/hooks/useEntitiesLayer';
import { useDeepLinkedEntitySelection } from '@/hooks/useDeepLinkedEntitySelection';
import { buildFindAndFixHandOff, FIND_AND_FIX_HAND_OFF_ROUTE } from '../utils/findAndFixHandOff';
import { fetchEntitiesForBounds, fetchEntitiesForNetworkMap } from '@/api/poisApi';
import { fetchNetworkExplorerEntitiesForBounds } from '@/api/networkExplorerApi';
import { useNetworkExplorerClustersLayer } from '@/hooks/useNetworkExplorerClustersLayer';
import { useNetworkExplorerClusterInteractions } from '@/hooks/useNetworkExplorerClusterInteractions';
import { useEntityMapFilters } from '@/hooks/useEntityMapFilters';
import { useMapClickDismissal } from '@/hooks/useMapClickDismissal';
import { usePoiInteractions } from '@/hooks/usePoiInteractions';
import { usePoiClusterInteractions } from '@/hooks/usePoiClusterInteractions';
import { usePoiNavigation } from '@/hooks/usePoiNavigation';
import { usePoiPipelines } from '@/hooks/usePoiPipelines';
import { usePoiRadiusLayer } from '@/hooks/usePoiRadiusLayer';
import { useRadialMeasureTool } from '@/hooks/useRadialMeasureTool';
import { useAirValvesLayer } from '@/hooks/useAirValvesLayer';
import { useTechnicianVansLayer } from '@/hooks/useTechnicianVansLayer';
import { useVehicleTripRouteLayer } from '@/hooks/useVehicleTripRouteLayer';
import type { DisplayedTripTrack } from '@/hooks/useVehicleTripRouteLayer';
import { useVanInteractions } from '@/hooks/useVanInteractions';
import { useAirValveInteractions } from '@/hooks/useAirValveInteractions';
import { useMapUrlSync } from '@/hooks/useMapUrlSync';
import { PoiPopup } from './PoiPopup';
import { RadialDistanceLabel } from './RadialDistanceLabel';
import { EXPLORER_CLUSTER_SUBTITLE, PoiClusterPopup } from './PoiClusterPopup';
import { PipelinePopup } from './PipelinePopup';
import { FittingPopup } from './FittingPopup';
import { VanPopup } from './VanPopup';
import { VanDetailsPanel } from './VanDetailsPanel';
import { AirValvePopup } from './AirValvePopup';
import { pickHoverCard, type HoverCardType } from '../utils/hoverCardPrecedence';
import { PoiDetailsPanel } from './PoiDetailsPanel';
import { LeakDetailsPanel } from './LeakDetailsPanel';
import { normaliseEntityType } from '../utils/entityStatus';
import { SaviMapKeyCard } from './SaviMapKeyCard';
import { DownloadHighRiskPanel } from './DownloadHighRiskPanel';
import { useMapFilters } from '@/hooks/useMapFilters';
import { fetchHighRiskAirValves } from '@/api/airValvesApi';
import { readHighRiskAirValveProperties } from '../utils/airValveFeatureProperties';
import type { HighRiskAirValveFeatureProperties } from '../utils/airValveFeatureProperties';

import { getBasemapDefinitions, resolveBasemap } from '../utils/basemaps';
import type { MapViewMode } from '../utils/basemaps';
import type {
    EntityToggleState,
    FeatureOverlayKey,
    FeatureOverlayState,
    MapMode,
    PoiTypeKey,
    PoiTypeState,
} from '../utils/mapLayers';
import { getEffectiveFeatureOverlays } from '../utils/mapLayers';
import type { PoiFeatureProperties } from '../utils/poiFeatureProperties';
import { loadMapPreferences, saveMapPreferences, type MapViewport } from '../utils/mapPreferences';
import { loadEntityMapPreferences, saveEntityMapPreferences } from '../utils/entityMapPreferences';
import {
    applyEntityFilters,
    DEFAULT_ENTITY_MAP_FILTER_STATE,
    serialiseEntityFiltersToParams,
} from '../utils/entityMapFilters';
import { useStatsFilters } from '@/stats/StatsFiltersContext';
import { deserializeEntityFiltersFromUrl, deserializeEntityLayersFromUrl } from '../utils/entityMapUrlState';
import { EntityFiltersPanel } from './EntityFiltersPanel';
import { saveLastProduct } from '@/clients/productPreference';
import { buildInitialMapStateFromSearch } from '../utils/mapUrlState';
import { DEFAULT_INITIAL_VIEW_STATE, EXPLORER_INITIAL_VIEW_STATE } from '../utils/mapDefaults';
import {
    AREA_FOCUS_ZOOM,
    flyToClientNetwork,
    flyToExplorerWorldView,
    getSemanticZoomMeta,
    mapLibreZoomToSemanticZoomLevel,
    semanticZoomLevelToMapLibreZoom,
    SemanticZoomLevel,
} from '../utils/semanticZoom';
import { calculateSemanticZoomDuration } from '../utils/mapAnimation';
import { MapSelectionStatsCard } from './MapSelectionStatsCard';
import { ORBIT_LAYER_IDS } from '../utils/mapLayerOrder';
import { isLayerVisibleAtSemanticLevel } from '../utils/semanticZoomLayers';
import { geometryCenter } from '@/utils/mapUtils';
import { SemanticZoomControl } from './SemanticZoomControl';
import { MapControls } from './MapControls';
import { MapLayersPanel } from './MapLayersPanel';
import { DownloadPanel } from './DownloadPanel';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useNetworkTableActions } from '@/hooks/useNetworkTableActions';
import { useTableView } from '@/table-view/useTableView';
import { NetworkTableRegion } from '@/components/table-view/NetworkTableRegion';
import './MapView.css';

const MAP_ERROR_MESSAGE = 'Map could not be loaded. Please check your connection or try another base layer.';

interface MapViewProps {
    /** Which product is hosting the map. Drives SAVI-specific controls, layers and detail cards. */
    mode?: MapMode;
}

export function MapView({ mode = 'find-and-fix' }: MapViewProps = {}) {
    const config = useAppConfig();
    const { selectedClient, clients, switchClient, canViewVehicleTracking, revokeVehicleTracking } = useClient();
    const isSavi = mode === 'savi';
    // Network Explorer: the cross-client globe. The client selected underneath stays untouched
    // (switching back restores it as it was), but nothing single-client may run here — every
    // client-scoped layer, panel and persistence path below stands down on this flag.
    const isExplorer = mode === 'network-explorer';
    // The split POI/leak entity experience is the Find & Fix map; SAVI has its own surfaces and
    // the explorer shows aggregated cells instead of individual entities.
    const entityMode = mode === 'find-and-fix';
    // Global Stats bar, handed down from the find-and-fix route so it renders above the map
    // only (every right-hand panel sits full-height beside it). Null on SAVI / mobile.
    const statsBarSlot = useStatsBarSlot();

    // Network Overview table layout (closed / split / maximised). Find & Fix only — inert
    // (always 'closed') on SAVI and in tests, where no TableViewProvider is mounted. A 50/50
    // split is unusable on a phone, so an open table always maximises below the desktop breakpoint.
    const tableView = useTableView();
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const tableLayout = !isDesktop && tableView.layout === 'split' ? 'maximised' : tableView.layout;
    const isTableMaximised = tableLayout === 'maximised';

    const basemaps = useMemo(() => getBasemapDefinitions(config), [config]);
    const basemapIds = useMemo(() => basemaps.map((b) => b.id), [basemaps]);

    // Read validated URL search params once before useState so they can seed
    // the initial values. useSearch({ strict: false }) works in any route.
    const search = useSearch({ strict: false });
    const navigate = useNavigate();
    const router = useRouter();
    const prefs = loadMapPreferences(basemapIds);
    // The explorer never restores the single-client camera: it opens on the globe at the world
    // view unless a shared link says otherwise. Basemap and overlay toggles are still shared.
    const initial = buildInitialMapStateFromSearch(
        search,
        isExplorer ? { ...prefs, viewport: undefined } : prefs,
        basemapIds,
    );
    const fallbackView = isExplorer ? EXPLORER_INITIAL_VIEW_STATE : DEFAULT_INITIAL_VIEW_STATE;

    const [selectedBasemapId, setSelectedBasemapId] = useState<string>(() => initial.selectedBasemapId);
    const [featureOverlays, setFeatureOverlays] = useState<FeatureOverlayState>(() => initial.featureOverlays);
    // Render-time view of the overlay state with overlays that don't belong to the current mode
    // forced off (e.g. Grid Tiles / Surveyed Areas can never show in SAVI). The raw `featureOverlays`
    // is kept for the Layers panel and persistence so user toggles survive switching modes.
    const effectiveFeatureOverlays = useMemo(
        () => getEffectiveFeatureOverlays(featureOverlays, mode, canViewVehicleTracking),
        [featureOverlays, mode, canViewVehicleTracking],
    );
    const [poiTypes, setPoiTypes] = useState<PoiTypeState>(() => initial.poiTypes);
    // The explorer is the globe: it opens in globe view unless a shared link pins another one.
    const [viewMode, setViewMode] = useState<MapViewMode>(() =>
        isExplorer && search.view === undefined ? 'globe' : initial.viewMode,
    );
    const [initialViewport] = useState<MapViewport | undefined>(() => initial.initialViewport);
    const filters = useMapFilters({ initialFromUrl: initial.filterState });
    // Split-model Layers/Filters state: the two entity toggles (persisted under the v2
    // preferences key, carried over from v1 on first load) and the entity filter model.
    // A shared-link URL wins over the persisted state; the entity layers codec treats
    // legacy per-status tokens as a v1-era link and falls back to both toggles on, and an
    // unparseable filters value (including a v1: payload) forces filters inactive.
    const [entityToggles, setEntityToggles] = useState<EntityToggleState>(() =>
        search.layers !== undefined
            ? deserializeEntityLayersFromUrl(search.layers).entityToggles
            : loadEntityMapPreferences(basemapIds).entityToggles,
    );
    const entityFilters = useEntityMapFilters({
        initialFromUrl:
            search.filters !== undefined
                ? (deserializeEntityFiltersFromUrl(search.filters) ?? {
                      isActive: false,
                      committed: DEFAULT_ENTITY_MAP_FILTER_STATE,
                  })
                : null,
    });
    const entityFilterFeatures = useCallback(
        (data: FeatureCollection) =>
            entityFilters.isActive ? applyEntityFilters(data, entityFilters.committed) : data,
        [entityFilters.isActive, entityFilters.committed],
    );

    // The AUTHORITATIVE open-state for this view's side panels: each panel renders from
    // `activePanel` directly, which also keeps them mutually exclusive with the Stats panel
    // in the route layout outside the <Outlet>. Both production map routes mount
    // ExclusivePanelProvider; the no-op fallback (always-null) exists for unit tests only.
    const { activePanel, setActivePanel } = useExclusivePanel();
    const isLayersPanelOpen = activePanel === 'layers';
    const isFiltersPanelOpen = activePanel === 'filters';
    const isDownloadPanelOpen = activePanel === 'download';
    const isMapKeyPanelOpen = activePanel === 'mapKey';
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [mapError, setMapError] = useState<string | null>(null);
    const initialZoom = initialViewport?.zoom ?? fallbackView.zoom;
    const [semanticZoomLevel, setSemanticZoomLevel] = useState<SemanticZoomLevel>(() =>
        mapLibreZoomToSemanticZoomLevel(initialZoom),
    );
    // Live MapLibre zoom — drives the semantic zoom control's mid-band connector fill.
    const [mapZoom, setMapZoom] = useState<number>(initialZoom);

    const activeApiRequestCount = useActiveApiRequestCount();

    const [showMapActivity, setShowMapActivity] = useState(false);

    const mapRef = useRef<MapRef>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Always true for the orbit map — canZoomAndDrag is not a prop here.
    // Held in a ref so useGlobeMode's restore callback reads the current value
    // without triggering unnecessary re-runs.
    const canZoomAndDragRef = useRef(true);

    // Tracks the current viewport so the prefs useEffect can include it without
    // wiping persisted viewport state before onMoveEnd has fired.
    const currentViewportRef = useRef<MapViewport | undefined>(initialViewport);

    // Skip camera animation on the very first load; initialViewState already
    // positions the camera correctly from the persisted view mode.
    const skipFirstCameraAnimRef = useRef(false);

    // Feature selection (POI details + AOI/DMA stats card) and located highlights are owned
    // by the map-interaction provider; MapView holds no interaction state of its own —
    // everything below is derived from the provider's selection/located records.
    const { selection: interactionSelection, located: interactionLocated, activeTool } = useMapInteraction();
    const interactionCommands = useMapInteractionCommands();
    // While the radial tool owns the map, every feature interaction stands down: POI/AOI/DMA
    // clicks and hovers, cluster click-to-zoom, SAVI valve interaction and the global click
    // dismissal all pause, and the tool's own listeners are the only click consumers.
    const isRadialToolActive = activeTool === 'radial';
    const selectedPoi = interactionSelection?.type === 'poi' ? interactionSelection.poi : null;
    const selectedPoiCoords = interactionSelection?.type === 'poi' ? interactionSelection.coords : null;
    // The selected POI's associated pipelines: one fetch per selection feeds both the map's
    // radius display and the panel's Pipelines tab. Deselecting (or switching client, or
    // activating a tool — both clear the selection) drops the data, which clears the radius.
    // Leak selections deliberately draw no radius: the circle belongs to the POI experience.
    const selectedLeak = interactionSelection?.type === 'leak' ? interactionSelection.leak : null;
    const selectedLeakCoords = interactionSelection?.type === 'leak' ? interactionSelection.coords : null;
    // The client the selected record belongs to. In the explorer the feature says (records of
    // several clients share the map); in Find & Fix it is the selected client. Every
    // per-record read (pipelines, history) takes it, so a cross-client record reads under
    // its owner.
    const selectedEntityClientId = (selectedPoi ?? selectedLeak)?.client_id ?? selectedClient?.id ?? null;
    const poiPipelines = usePoiPipelines(selectedPoi?.id ?? null, selectedEntityClientId, entityMode || isExplorer);
    usePoiRadiusLayer(mapRef, isMapLoaded, poiPipelines.data);
    const selectedVan = interactionSelection?.type === 'van' ? interactionSelection.van : null;
    const locatedPoiId = interactionLocated?.feature.type === 'poi' ? interactionLocated.feature.id : null;
    // Clicked AOI/DMA whose floating stats card is shown. Independent of the side panels
    // (ExclusivePanelContext) — only one card is ever shown at a time. The aoi/dma variants of
    // the provider's SelectedFeature ARE MapSelection, so the card machinery consumes them
    // as-is; the poi/van variants have their own panels and are excluded by allow-list.
    const selection: MapSelection | null =
        interactionSelection !== null && (interactionSelection.type === 'aoi' || interactionSelection.type === 'dma')
            ? interactionSelection
            : null;
    const locatedAreaName = interactionLocated?.feature.type === 'aoi' ? interactionLocated.feature.name : null;
    // DMA highlight: a located DMA (table Locate / DMA search) or a card-selected one. The
    // reducer invariant means the two can never coexist — the ?? is a safety net, not a tiebreak.
    const selectedDmaIdent =
        interactionLocated?.feature.type === 'dma'
            ? interactionLocated.feature.ident
            : selection?.type === 'dma'
              ? selection.ident
              : null;

    const { hoveredPoi, hoveredPoiLngLat, onHoverCardEnter, onHoverCardLeave } = usePoiInteractions(
        mapRef,
        isMapLoaded,
        {
            onPoiClick: (poi, coords) => {
                // Any uncommitted filter draft dies with the panel it was typed into.
                entityFilters.discardDraft();
                // Route by entity family: a leak feature opens the leak details panel,
                // never the POI one. Features without an entity_type are POIs (the wire
                // tolerance rule).
                if (normaliseEntityType(poi.entity_type) === 'leak') {
                    interactionCommands.selectLeak(poi, coords);
                } else {
                    interactionCommands.selectPoi(poi, coords);
                }
                // Opening the details panel closes whichever side panel is open
                // (including the route's stats panel — cross-Outlet exclusivity).
                setActivePanel(null);
            },
        },
        (entityMode || isExplorer) && !isRadialToolActive,
    );

    // Hovering a POI cluster bubble (network-level or viewport) shows the per-status
    // breakdown card. The entity layers draw in Find & Fix and, from the POI band inward,
    // in the explorer; SAVI has neither.
    const { hoveredEntitySummary, hoveredClusterLngLat, onClusterCardEnter, onClusterCardLeave } =
        usePoiClusterInteractions(mapRef, isMapLoaded, (entityMode || isExplorer) && !isRadialToolActive);

    // Selecting a POI (via search or next/prev navigation) opens the details panel (derived
    // from the selection) and closes any open side panel; clearing needs no panel writes.
    // Same entity-family routing as a map click: a leak search result opens the leak
    // details panel, never the POI one.
    const handleSelectPoi = (poi: PoiFeatureProperties | null, coords?: [number, number]) => {
        if (poi) {
            if (normaliseEntityType(poi.entity_type) === 'leak') {
                interactionCommands.selectLeak(poi, coords ?? null);
            } else {
                interactionCommands.selectPoi(poi, coords ?? null);
            }
            setActivePanel(null);
        } else {
            interactionCommands.clearSelection();
        }
    };

    // "Open in Find & Fix" on an explorer record opens that client's Find & Fix in a NEW tab
    // (the designer's call: the explorer, and the panel the user is reading, stay exactly as
    // they are). The URL carries the client, the viewport and the record's identity, so the new
    // tab boots on the record's client (ClientProvider honours `?client=` over the remembered
    // one) and its Find & Fix map selects the record on arrival (useDeepLinkedEntitySelection).
    // If the browser refuses the new window (a pop-up blocker), the hand-off happens in this
    // tab instead: switch to the record's client FIRST (the switcher's precedent, so the product
    // route mounts on that client and its module check sees the right one), then navigate.
    const handleOpenInFindAndFix = (entity: PoiFeatureProperties, coords: [number, number] | null) => {
        const handOff = buildFindAndFixHandOff(entity, coords, clients);
        if (handOff.kind === 'client-unavailable') {
            showErrorToast({
                code: 'forbidden',
                title: 'Access denied',
                message: 'This record’s network is not available to you.',
            });
            return;
        }
        if (handOff.kind === 'module-missing') {
            toast.info(`${handOff.client.name} does not have Find & Fix.`);
            return;
        }
        const { href } = router.buildLocation({ to: FIND_AND_FIX_HAND_OFF_ROUTE, search: handOff.search });
        // Not the `noopener` feature: that makes window.open return null, which is also the
        // blocked signal. Same origin, so severing the opener by hand is enough.
        const opened = window.open(href, '_blank');
        if (opened) {
            opened.opener = null;
            return;
        }
        if (selectedClient?.id !== handOff.client.id) switchClient(handOff.client.id);
        void navigate({ to: FIND_AND_FIX_HAND_OFF_ROUTE, search: handOff.search });
    };

    // The other end of that hand-off: a Find & Fix URL naming a record selects it once the
    // switched client has landed and the map is up. Inert without the params.
    useDeepLinkedEntitySelection(
        search,
        selectedClient?.id ?? null,
        entityMode && isMapLoaded,
        interactionSelection !== null,
        handleSelectPoi,
    );

    // Closing the details panel clears the selection; the panel signal needs no release —
    // details-open is derived from the selection, never from activePanel.
    const handleClearSelection = () => {
        interactionCommands.clearSelection();
    };

    // Single entry point for map-click AOI/DMA selection: dispatches to the provider, which
    // replaces any existing card AND any located highlight — one interaction at a time.
    // Empty-map clearing never arrives here: the global click listener below owns that
    // decision for the whole map, so selection and dismissal can never race on one click.
    const handleSelectFeature = useCallback(
        (next: MapSelection) => {
            if (next.type === 'aoi') {
                interactionCommands.selectArea(next.name, next.lngLat);
            } else {
                interactionCommands.selectDma(next.ident, next.name, next.lngLat);
            }
        },
        [interactionCommands],
    );

    // Closing the stats card clears the selection only — deliberately narrower than an
    // empty-map click, which also drops located highlights.
    const handleCloseSelectionCard = useCallback(() => {
        interactionCommands.clearSelection();
    }, [interactionCommands]);

    // DMA search result: highlight + fit, no stats card — a locate, not a selection
    // (the established DMA-search precedent). Search-originated, so clearing the search
    // releases it (SEARCH_CLEARED) while table-originated locates survive.
    const handleSearchDmaSelect = useCallback(
        (ident: string) => {
            interactionCommands.locateDma(ident, 'search');
        },
        [interactionCommands],
    );

    const handleSearchCleared = useCallback(() => {
        interactionCommands.searchCleared();
    }, [interactionCommands]);

    // Drives the surveyed-area selection highlight layers. `selection` (a map click, which also
    // opens the stats card) is one driver; a table Locate is the other, and must highlight WITHOUT
    // opening the card — the same multi-driver shape as selectedDmaIdent (click + search).
    const selectedAreaName = locatedAreaName ?? (selection?.type === 'aoi' ? selection.name : null);

    const selectionStats = useSelectionStats(selection, selectedClient?.id ?? null);

    // A clicked AOI/DMA also drives the Global Stats bar: while one is selected the bar shows
    // that feature (overriding the map-centre resolution below); clearing it falls back to the
    // centre. Memoised on `selection` so panning (which re-renders but leaves the selection
    // object identity intact) doesn't retrigger scope resolution.
    const selectionScope = useMemo<StatsScope | null>(() => {
        if (!selection) return null;
        return selection.type === 'aoi'
            ? { type: 'area', name: selection.name }
            : { type: 'dma', ident: selection.ident, name: selection.name };
    }, [selection]);

    useGlobeMode(mapRef, isMapLoaded, viewMode, canZoomAndDragRef);
    // Find & Fix keeps AOI/DMA polygons loaded even when their overlays are toggled
    // off (keepDataLoaded) so the stats-scope resolver below can do centre-point
    // containment independent of rendering visibility.
    const { getDmaGeometry, getDmasData, dmasDataVersion } = useDistrictMeterAreasLayer(
        mapRef,
        isMapLoaded,
        selectedClient,
        effectiveFeatureOverlays,
        selectedDmaIdent,
        entityMode,
    );
    useDmaInteractions(mapRef, isMapLoaded);
    useAreaInteractions(mapRef, isMapLoaded);
    useFeatureSelection(mapRef, isMapLoaded, handleSelectFeature, !isRadialToolActive);
    const { getAreaGeometry, getAreasData, areasDataVersion } = useSurveyedAreasLayer(
        mapRef,
        isMapLoaded,
        selectedClient,
        effectiveFeatureOverlays,
        selectedAreaName,
        entityMode,
    );

    // Publishes the AOI/DMA under the map centre to the Global Stats bar in the
    // route layout. No-op in SAVI and when rendered without a StatsScopeProvider.
    const { setScope } = useStatsScope();

    // Publishes the committed map filters to the Global Stats bar's fetch hooks —
    // the bar must describe the same filtered population the map renders. Inert
    // without a StatsFiltersProvider (SAVI, tests) and null when filters are off.
    const { setFilters: setStatsFilters } = useStatsFilters();
    useEffect(() => {
        setStatsFilters(
            entityMode && entityFilters.isActive ? serialiseEntityFiltersToParams(entityFilters.committed) : null,
        );
    }, [entityMode, entityFilters.isActive, entityFilters.committed, setStatsFilters]);
    const { resetToNetworkScope } = useStatsScopeResolver({
        mapRef,
        isMapLoaded,
        enabled: entityMode,
        semanticZoomLevel,
        clientId: selectedClient?.id ?? null,
        getAreasData,
        areasDataVersion,
        getDmasData,
        dmasDataVersion,
        selectionScope,
        setScope,
    });

    // Network Explorer: the estate's pre-aggregated cells clustered into bubbles, and the
    // click-pinned breakdown card. Both inert outside the explorer. The POI / Leak toggles
    // choose which family the bubbles count — the headline statistics never follow them.
    useNetworkExplorerClustersLayer(mapRef, isMapLoaded, entityToggles, isExplorer);
    // The bubbles behave like the single-client clusters: hover shows the breakdown card,
    // click zooms in. Their card and the viewport clusters' card are never up together (the
    // bubbles stop at the POI band, where the viewport clusters start).
    const explorerBubbles = useNetworkExplorerClusterInteractions(
        mapRef,
        isMapLoaded,
        isExplorer && !isRadialToolActive,
    );

    // Back to the network view: fly out, drop any clicked AOI/DMA (which closes its stats card
    // and highlight) and return the Global Stats bar to network totals. Clearing the selection is
    // what closes the scoped helper strip — a clicked feature pins the scope, so flying out alone
    // would leave the bar showing the old feature. The scope reset is held until the flight's
    // moveend, so the clearing can't re-resolve the old feature from the still-zoomed-in centre;
    // with no focal point there is no flight (and no moveend), so the centre resolution takes over.
    const handleResetToNetwork = useCallback(() => {
        if (isExplorer) {
            // The explorer's "network" is the globe: back out to the world view (the flight
            // itself drops any bubble card). There is no client scope to reset and no focal
            // point to miss.
            flyToExplorerWorldView(mapRef.current, EXPLORER_INITIAL_VIEW_STATE);
            interactionCommands.resetToNetwork();
            return;
        }
        const flew = flyToClientNetwork(mapRef.current, selectedClient?.focalPoint);
        if (!flew) toast.info('No network location is configured for this client');
        // "Back to the network" means back to a clean slate: the stats card, an open POI
        // details panel and any located highlight all clear — RESET_TO_NETWORK is the
        // whole-slice transition, unlike a card close (CLEAR_SELECTION) or an empty click.
        interactionCommands.resetToNetwork();
        if (flew) resetToNetworkScope();
    }, [isExplorer, mapRef, selectedClient, interactionCommands, resetToNetworkScope]);

    // Registers this map's imperative actions with the route-level bridge so out-of-tree UI (the
    // stats bar's Reset control) can drive the map. Both it and the locate-network map control
    // dispatch the same reset. Inert without a provider (savi route, tests).
    const { register: registerMapActions } = useMapActions();
    useEffect(() => {
        registerMapActions({ flyToNetwork: handleResetToNetwork });
        return () => {
            registerMapActions(null);
        };
    }, [registerMapActions, handleResetToNetwork]);
    usePipelinesLayer(mapRef, isMapLoaded, selectedClient, effectiveFeatureOverlays);
    const { hoveredPipeline, hoveredPipelineLngLat } = usePipelineInteractions(mapRef, isMapLoaded);
    const { hoveredFitting, hoveredFittingLngLat } = useFittingInteractions(mapRef, isMapLoaded);
    useGridTilesLayer(mapRef, isMapLoaded, selectedClient, effectiveFeatureOverlays);
    useFittingsLayer(mapRef, isMapLoaded, selectedClient, effectiveFeatureOverlays);
    // Whose records the entity layers draw. Find & Fix draws the selected client's, with the
    // whole-network feed behind the wide-end clusters; the explorer draws every included
    // client's and has no network feed, because its cells layer owns the wide end. Memoised:
    // the hook treats a new object as a new feed set and drops its caches, so the explorer's
    // feeds must not key on the client selected underneath it (the switcher and the blocked
    // hand-off fallback both change it while the explorer is still mounted).
    const feedClient = isExplorer ? null : selectedClient;
    const entityFeeds = useMemo<EntityFeeds | null>(() => {
        if (isExplorer) return { key: 'network-explorer', fetchViewport: fetchNetworkExplorerEntitiesForBounds };
        if (!feedClient) return null;
        const clientId = feedClient.id;
        return {
            key: clientId,
            fetchViewport: (bbox) => fetchEntitiesForBounds(clientId, bbox),
            fetchNetwork: () => fetchEntitiesForNetworkMap(clientId),
        };
    }, [isExplorer, feedClient]);
    // Entity layers (POI pins + leak markers): Find & Fix, and the explorer from the POI band
    // inward (its cells layer owns the wide end). Inert (no fetch, no render) in SAVI, where
    // the air-valve product owns the map.
    useEntitiesLayer(
        mapRef,
        isMapLoaded,
        entityFeeds,
        entityToggles,
        // The halo has two derived drivers: a table Locate (highlight only) and a selection
        // (which also opens the details panel). Both come from the interaction provider, whose
        // reducer guarantees they never coexist — the ?? here is a safety net, not a tiebreak.
        // Leak selections drive the same shared highlight via their own (leak:-prefixed) id.
        locatedPoiId ?? selectedPoi?.id ?? selectedLeak?.id ?? null,
        entityFilterFeatures,
        entityMode || isExplorer,
        !isRadialToolActive,
    );
    // Worklist Previous/Next is a single-client idea: the explorer's panels hide it and no
    // worklist is fetched there.
    const { navigateNext, navigatePrevious, canNavigate } = usePoiNavigation(
        mapRef,
        selectedPoi,
        handleSelectPoi,
        isExplorer ? null : (selectedClient?.id ?? null),
    );

    // How Locate gets the map back on screen when the table is covering it.
    //
    // On desktop that is the 50/50 split, so the table stays visible beside the map. On mobile
    // there IS no split — an open table is deliberately always full-screen (see tableLayout above),
    // so restoring it would be re-clamped straight back to maximised and Locate would fly a map the
    // user cannot see. Closing the table is the only way to reveal the map there, and it is what
    // Locate is asking for.
    //
    // The mobile close goes through tableClosed('reveal'), which is a deliberate no-op on the
    // interaction slice: the located highlight is the whole point of this close and must survive it,
    // unlike the close BUTTON below. Dispatching it rather than closing silently is what states that
    // rule where the close happens — routing this path through handleCloseTableView instead would
    // clear the very highlight Locate just set, and nothing but this call marks that as wrong.
    // NOT TESTED: the branch is a one-line ternary over two context actions; covering it would mean
    // driving the whole MapView at two viewports for no assertion the hook's own tests do not make.
    // useNetworkTableActions tests the reveal-then-navigate sequence against `isMapHidden`, and the
    // reducer's own suite pins 'reveal' against 'explicit'.
    const revealMapOnMobile = useCallback(() => {
        interactionCommands.tableClosed('reveal');
        tableView.close();
    }, [interactionCommands, tableView]);
    const revealMap = isDesktop ? tableView.restoreSplit : revealMapOnMobile;

    const tableActions = useNetworkTableActions({
        mapRef,
        isMapHidden: isTableMaximised,
        revealMap,
        locatedPoiId,
        // Locating clears any open details panel through the provider and never touches the
        // side panels. A null name/ident is the Unassigned bucket: nothing to highlight, so
        // the provider state is simply cleared — same end-state as a locate whose feature
        // has no polygon.
        locatePoi: (id) => {
            interactionCommands.locatePoi(id);
        },
        locateArea: (name) => {
            if (name !== null) interactionCommands.locateArea(name);
            else interactionCommands.emptyMapClick();
        },
        locateDma: (ident) => {
            if (ident !== null) interactionCommands.locateDma(ident);
            else interactionCommands.emptyMapClick();
        },
        showPoiDetails: (poi, coords) => {
            // A child row's Show Details opens the leak details panel — no surface presents
            // a recorded leak as a Point of Interest. Root rows keep the POI panel.
            if (normaliseEntityType(poi.entity_type) === 'leak') {
                interactionCommands.selectLeak(poi, coords);
            } else {
                interactionCommands.showPoiDetails(poi, coords);
            }
            // Opening details closes whichever side panel is open, as a map click does.
            setActivePanel(null);
        },
    });

    // Closing the table clears the highlights it created — they have no owner once it is gone.
    // TABLE_CLOSED('explicit') drops ALL table-originated located state (POI halo, Area fill,
    // DMA outline) and any open POI details (Show Details is a maximised-only, table-only path
    // into it). Search-originated highlights are not the table's and survive.
    const handleCloseTableView = () => {
        interactionCommands.tableClosed('explicit');
        tableView.close();
    };

    // The map-control toggle must close through the SAME cleanup path as the table's close
    // button — closing via a raw tableView.toggle would leave Locate highlights orphaned.
    const handleToggleTableView = () => {
        if (tableView.isOpen) {
            handleCloseTableView();
        } else {
            tableView.open();
        }
    };

    // "View" action on the selection stats card: fly to the selected AOI/DMA (centred on its
    // centroid) at that feature's own semantic-level mid-stop — AOIs to Stop 4 (AREA_FOCUS_ZOOM,
    // 9.75) so the area stays visible and the Global Stats bar scopes to it; DMAs to Stop 6 (the
    // District Meter Area mid-stop, 13.5). Each target is exactly where the card's "View" button
    // then hides, so pressing it lands you at the hand-off point. We only zoom *in*: if the map is
    // already at/past the target the current zoom is kept and we just recentre. Centring uses the
    // full unclipped geometry from the layer hooks (falling back to the click point if it isn't
    // loaded). The card itself stays open — now that it is pinned to the corner (not anchored to the
    // feature) it should remain visible after flying; only its "View" button hides at the target.
    const handleViewSelection = () => {
        if (!selection) return;
        const map = mapRef.current;
        if (!map) return;

        const geometry = selection.type === 'aoi' ? getAreaGeometry(selection.name) : getDmaGeometry(selection.ident);
        const center = geometryCenter(geometry) ?? selection.lngLat;

        const focusZoom =
            selection.type === 'aoi'
                ? AREA_FOCUS_ZOOM
                : getSemanticZoomMeta(SemanticZoomLevel.DistrictMeterArea).midStop;
        const zoom = Math.max(map.getZoom(), focusZoom);

        map.flyTo({ center, zoom });
    };

    // The stats card's "View …" button is hidden once the map is zoomed in to the selection's own
    // semantic-level mid-stop — past it flying to the feature would only recentre. AOIs hide from
    // Stop 4 (Area of Interest mid, 9.75); DMAs from Stop 6 (District Meter Area mid, 13.5).
    const selectionViewLevel =
        selection?.type === 'dma' ? SemanticZoomLevel.DistrictMeterArea : SemanticZoomLevel.AreaOfInterest;
    const showSelectionViewButton = mapZoom < getSemanticZoomMeta(selectionViewLevel).midStop;

    // SAVI air-valve layers + hover/click detail. Inert (no fetch, no listeners) outside SAVI.
    useAirValvesLayer(mapRef, isMapLoaded, selectedClient, effectiveFeatureOverlays, isSavi, !isRadialToolActive);

    // Live technician-van positions, per-user capability gated. A mid-session 403 revokes
    // the capability, which cascades: polling stops, the source clears, the Layers-panel row
    // disappears and the effective overlay state forces the layer off.
    useTechnicianVansLayer(
        mapRef,
        isMapLoaded,
        selectedClient,
        effectiveFeatureOverlays,
        canViewVehicleTracking && !isSavi,
        revokeVehicleTracking,
        selectedVan?.id ?? null,
    );
    const { hoveredVan, hoveredVanLngLat } = useVanInteractions(mapRef, isMapLoaded, {
        onVanClick: (van, coords) => {
            if (isRadialToolActive) return;
            // Any uncommitted filter draft dies with the panel it was typed into.
            entityFilters.discardDraft();
            interactionCommands.selectVan(van, coords);
            // Opening the details panel closes whichever side panel is open.
            setActivePanel(null);
        },
    });

    // A selected van cannot outlive its marker: capability revoke, a switch to SAVI or the
    // user toggling the vans overlay off all clear the selection (and close the panel).
    const vansVisible = canViewVehicleTracking && !isSavi && effectiveFeatureOverlays.technicianVans;
    useEffect(() => {
        if (vansVisible) return;
        if (interactionSelection?.type === 'van') interactionCommands.clearSelection();
    }, [vansVisible, interactionSelection, interactionCommands]);

    // The drawn trip route belongs to the selected van's panel session: deselecting the van,
    // selecting another one, or losing the capability (which clears the selection above) all
    // remove it from the map.
    const [displayedTripTrack, setDisplayedTripTrack] = useState<DisplayedTripTrack | null>(null);
    const selectedVanId = selectedVan?.id ?? null;
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect, react-x/set-state-in-effect
        setDisplayedTripTrack(null);
    }, [selectedVanId]);
    useVehicleTripRouteLayer(mapRef, isMapLoaded, displayedTripTrack);

    // Applies overlay + semantic-zoom visibility. Declared AFTER every layer-registration hook
    // above so its initial pass runs once those layers exist — otherwise, on a fresh load at a
    // coarse level (e.g. Network Overview), layers registered as "visible" (like DMAs) would not
    // be hidden until the next semantic-level change, leaving them showing at the wrong zoom.
    useMapLayerVisibility(
        mapRef,
        isMapLoaded,
        effectiveFeatureOverlays,
        poiTypes,
        semanticZoomLevel,
        // Entity mode (and the explorer, whose bubbles and markers are POI-family layers too)
        // drives those layers from the two entity toggles; the legacy per-status poiTypes
        // state is meaningless there (an entity-era URL zeroes it).
        entityMode || isExplorer ? entityToggles : undefined,
    );
    const {
        activeValve,
        activeCoords,
        onCardEnter: onAirValveCardEnter,
        onCardLeave: onAirValveCardLeave,
        clearSelection: clearAirValveSelection,
    } = useAirValveInteractions(mapRef, isMapLoaded, isSavi && !isRadialToolActive);

    // One authoritative decision per physical map click: a single global listener
    // classifies the click from one rendered-feature query over the named handled-layer
    // list and owns every cross-feature clearing outcome. Feature hooks keep selection,
    // pinning and cluster-zoom ownership; only clearing/dismissal is coordinated here.
    // Clearing and the cluster zoom are two effects of the same click transaction — no
    // ordering between them is assumed or required.
    // Every callback stands down while the radial tool is active: the click is the tool's
    // input (centre placement / radius freeze), never a dismissal or feature interaction.
    // The callbacks are read per-event through a ref, so this render's guard is always fresh.
    useMapClickDismissal(mapRef, isMapLoaded, {
        // Truly empty: clears the selection and any located highlight, and dismisses a
        // pinned valve card. Open side panels stay.
        onEmptyClick: () => {
            if (isRadialToolActive) return;
            interactionCommands.emptyMapClick();
            clearAirValveSelection();
        },
        // An AOI/DMA click replaces the selection itself (handleSelectFeature); only the
        // SAVI valve card needs dismissing so a single feature card stays active.
        onPolygonClick: () => {
            if (isRadialToolActive) return;
            clearAirValveSelection();
        },
        // A valve click closes any AOI/DMA stats card; the delegated valve handler pins
        // the valve card itself (replacing any previous pin).
        onValveClick: () => {
            if (isRadialToolActive) return;
            interactionCommands.clearSelection();
        },
        // A cluster click begins a new feature interaction: any open card (POI details,
        // AOI/DMA stats, pinned valve) closes while the cluster handler zooms.
        onPoiClusterClick: () => {
            if (isRadialToolActive) return;
            interactionCommands.clearSelection();
        },
        onValveClusterClick: () => {
            if (isRadialToolActive) return;
            interactionCommands.clearSelection();
            clearAirValveSelection();
        },
    });

    // The radial measuring tool. Declared after every feature-interaction hook above so that
    // when activation disables them, their cleanups (which reset the canvas cursor) run before
    // this hook's effect sets the crosshair — last writer wins on the canvas style.
    const radialMeasurement = useRadialMeasureTool(
        mapRef,
        isMapLoaded,
        isRadialToolActive,
        interactionCommands.deactivateTool,
    );

    function handleToggleRadialTool() {
        if (isRadialToolActive) {
            interactionCommands.deactivateTool();
        } else {
            interactionCommands.activateTool('radial');
        }
    }

    // High-risk air valves drive both the legend/Layers count and the high-risk detail card
    // (the viewport feed only flags `highRisk`; date/reasons come from this /hr feed, matched
    // onto a hovered valve by asset name).
    const [highRiskFeatures, setHighRiskFeatures] = useState<FeatureCollection | null>(null);
    useEffect(() => {
        if (!isSavi || !selectedClient) {
            // eslint-disable-next-line react-hooks/set-state-in-effect, react-x/set-state-in-effect
            setHighRiskFeatures(null);
            return;
        }
        let cancelled = false;
        fetchHighRiskAirValves(selectedClient.id)
            .then((data) => {
                if (!cancelled) setHighRiskFeatures(data);
            })
            .catch(() => {
                if (!cancelled) setHighRiskFeatures(null);
            });
        return () => {
            cancelled = true;
        };
    }, [isSavi, selectedClient]);

    const highRiskCount = highRiskFeatures ? highRiskFeatures.features.length : null;
    const highRiskByName = useMemo(() => {
        // globalThis.Map — the bare `Map` identifier is the react-map-gl component import.
        const byName = new globalThis.Map<string, HighRiskAirValveFeatureProperties>();
        if (highRiskFeatures) {
            for (const feature of highRiskFeatures.features) {
                const props = readHighRiskAirValveProperties(feature.properties);
                if (props.name) byName.set(props.name, props);
            }
        }
        return byName;
    }, [highRiskFeatures]);
    const activeHighRiskDetail = activeValve?.highRisk ? (highRiskByName.get(activeValve.name) ?? null) : null;

    const isPoiDetailsPanelOpen = selectedPoi !== null;

    const { syncViewportToUrl } = useMapUrlSync({
        mapRef,
        isMapLoaded,
        // Cold start (no shared-link/localStorage viewport) → let the focal-point effect fly to
        // the client network; otherwise respect the restored viewport.
        hasRestoredViewport: initialViewport !== undefined,
        // The explorer shows every client: its URL names none, and neither its URL nor its
        // camera reacts to the client selected underneath it.
        followsClient: !isExplorer,
        selectedBasemapId,
        viewMode,
        featureOverlays,
        poiTypes,
        validBasemapIds: basemapIds,
        setSelectedBasemapId,
        setViewMode,
        setFeatureOverlays,
        setPoiTypes,
        filterIsActive: filters.isActive,
        filterCommitted: filters.committed,
        setFilterFromUrl: filters.setFromUrl,
        // The explorer's bubbles and markers are driven by the entity toggles, so its URL
        // must carry them too (the legacy codec would write poiTypes, which it never uses,
        // and a shared link would read back as "both on").
        entityMode: entityMode || isExplorer,
        entityToggles,
        setEntityToggles,
        entityFilterIsActive: entityFilters.isActive,
        entityFilterCommitted: entityFilters.committed,
        setEntityFilterFromUrl: entityFilters.setFromUrl,
    });

    // The Stats panel (owned by the route layout) opening closes this view's panels for free —
    // they all render from the one activePanel value. What remains here: discard any
    // uncommitted filter draft (Filters may have been the panel it displaced) and close the
    // POI details panel (PANEL_OPENED). Keys on activePanel only and never calls
    // setActivePanel, so it cannot loop. Inert without a provider (activePanel stays null).
    useEffect(() => {
        if (activePanel !== 'stats') return;
        entityFilters.discardDraft();
        interactionCommands.panelOpened();
        // eslint-disable-next-line react-hooks/exhaustive-deps, react-x/exhaustive-deps
    }, [activePanel]);

    // Dismiss the AOI/DMA stats card on Escape.
    useEffect(() => {
        if (!selection) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleCloseSelectionCard();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [selection, handleCloseSelectionCard]);

    // Clear the card when the selected feature's layer is hidden by semantic zoom, so it
    // never floats over a feature the user can no longer see.
    useEffect(() => {
        if (!selection) return;
        const layerId = selection.type === 'dma' ? ORBIT_LAYER_IDS.dmaFill : ORBIT_LAYER_IDS.surveyedAreasFill;
        if (!isLayerVisibleAtSemanticLevel(layerId, semanticZoomLevel)) {
            // Guarded on a non-null selection, so the CLEAR_SELECTION dispatch cannot loop.
            interactionCommands.clearSelection();
        }
    }, [semanticZoomLevel, selection, interactionCommands]);

    // Animate the camera when the user switches between 2D, 3D, and globe.
    useEffect(() => {
        if (!isMapLoaded) return;

        if (!skipFirstCameraAnimRef.current) {
            skipFirstCameraAnimRef.current = true;
            return;
        }

        const map = mapRef.current;
        if (!map) return;

        map.easeTo({
            pitch: viewMode === '3d' ? 60 : 0,
            bearing: viewMode === '3d' ? 20 : 0,
            duration: 600,
        });
    }, [viewMode, isMapLoaded]);

    // Watch the map container for size changes caused by the flex layout
    // reacting to side panels opening or closing. The panels are flex siblings
    // of this container and use a CSS width transition (200 ms), so the
    // ResizeObserver fires on every animation frame for the duration of that
    // transition — roughly 12–15 times.
    //
    // map.resize() is debounced by 50 ms so that MapLibre only repaints once,
    // after the container has stabilised. Without debouncing, each of those
    // 12–15 callbacks would trigger a full canvas repaint, producing the
    // visible map flash reported during panel open/close.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let resizeTimer: ReturnType<typeof setTimeout> | null = null;

        const ro = new ResizeObserver(() => {
            if (resizeTimer !== null) clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                mapRef.current?.resize();
            }, 50);
        });

        ro.observe(container);
        return () => {
            ro.disconnect();
            if (resizeTimer !== null) clearTimeout(resizeTimer);
        };
    }, []);

    // The explorer's globe camera and view mode must never leak into the single-client map's
    // remembered state (a later Find & Fix load would open on a zoom-1.6 globe and skip the
    // focal-point fly-to), so an explorer session persists nothing.
    useEffect(() => {
        if (isExplorer) return;
        saveMapPreferences({
            selectedBasemapId,
            viewMode,
            featureOverlays,
            poiTypes,
            viewport: currentViewportRef.current,
        });
    }, [isExplorer, selectedBasemapId, viewMode, featureOverlays, poiTypes]);

    // The split-model preferences persist in parallel under the v2 key; the v1 record above
    // stays intact for rollback until the post-release cleanup removes it.
    useEffect(() => {
        if (isExplorer) return;
        saveEntityMapPreferences({
            selectedBasemapId,
            viewMode,
            featureOverlays,
            entityToggles,
            viewport: currentViewportRef.current,
        });
    }, [isExplorer, selectedBasemapId, viewMode, featureOverlays, entityToggles]);

    // Remember the active product so a bare load (no product in the URL) can restore it.
    // MapView only mounts once its ProductGuard has granted access, so we only ever persist
    // a product the current client can actually use. The explorer is a mode, not a product:
    // it has its own landing rule and is never remembered here.
    useEffect(() => {
        if (mode === 'find-and-fix' || mode === 'savi') saveLastProduct(mode);
    }, [mode]);

    useEffect(() => {
        const shouldShow = isMapLoaded && activeApiRequestCount > 0;
        const delay = shouldShow ? 250 : 1500;
        const id = window.setTimeout(() => {
            setShowMapActivity(shouldShow);
        }, delay);
        return () => {
            window.clearTimeout(id);
        };
    }, [isMapLoaded, activeApiRequestCount]);

    function handleSemanticZoomChange(level: SemanticZoomLevel) {
        if (!isMapLoaded) return;

        const map = mapRef.current?.getMap();
        if (!map) return;

        const targetZoom = semanticZoomLevelToMapLibreZoom(level);
        const currentZoom = map.getZoom();

        const reducedMotion =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const duration = reducedMotion ? 0 : calculateSemanticZoomDuration(currentZoom, targetZoom);

        map.easeTo({ zoom: targetZoom, duration });
        setSemanticZoomLevel(level);
    }

    // Panel handlers: visibility is a single setActivePanel write — opening one panel closes
    // the others structurally. Panel-specific side effects stay composed here, in the owning
    // handler, never in the interaction provider:
    //   - entityFilters.discardDraft() whenever a panel change displaces or closes the Filters panel
    //     (an uncommitted draft must not survive out of sight);
    //   - interactionCommands.panelOpened() on every OPEN (closes POI details, keeps a card);
    //   - clearAirValveSelection() where the SAVI air-valve card would float over the panel.
    function handleToggleLayersPanel() {
        if (!isLayersPanelOpen) {
            entityFilters.discardDraft();
            interactionCommands.panelOpened();
            clearAirValveSelection();
            setActivePanel('layers');
        } else {
            setActivePanel(null);
        }
    }

    function handleToggleFiltersPanel() {
        // No filters surface in SAVI (the control is hidden too — this guards the table
        // header's shortcut and any future caller).
        if (!entityMode) return;
        if (isFiltersPanelOpen) {
            // Closing Filters — discard uncommitted draft changes.
            entityFilters.discardDraft();
            setActivePanel(null);
        } else {
            // Opening Filters — sync the draft from committed values.
            entityFilters.discardDraft();
            interactionCommands.panelOpened();
            setActivePanel('filters');
        }
    }

    function handleCloseFiltersPanel() {
        entityFilters.discardDraft();
        setActivePanel(null);
    }

    function handleToggleDownloadPanel() {
        if (isDownloadPanelOpen) {
            setActivePanel(null);
        } else {
            interactionCommands.panelOpened();
            clearAirValveSelection();
            setActivePanel('download');
        }
    }

    function handleCloseDownloadPanel() {
        setActivePanel(null);
    }

    function handleToggleMapKeyPanel() {
        if (isMapKeyPanelOpen) {
            setActivePanel(null);
        } else {
            interactionCommands.panelOpened();
            setActivePanel('mapKey');
        }
    }

    function handleCloseMapKeyPanel() {
        setActivePanel(null);
    }

    function handleBasemapChange(id: string) {
        setSelectedBasemapId(id);
        setMapError(null);
    }

    function handleViewModeChange(mode: MapViewMode) {
        setViewMode(mode);
    }

    function handleFeatureOverlayChange(key: FeatureOverlayKey, enabled: boolean) {
        setFeatureOverlays((prev) => ({ ...prev, [key]: enabled }));
    }

    function handlePoiTypeChange(key: PoiTypeKey, enabled: boolean) {
        setPoiTypes((prev) => ({ ...prev, [key]: enabled }));
    }

    const activeBasemap = resolveBasemap(basemaps, selectedBasemapId);

    // Seed the initial camera from persisted viewport, falling back to viewMode
    // defaults so the map starts correctly without a post-load animation.
    const initialPitch = initialViewport?.pitch ?? (viewMode === '3d' ? 60 : 0);
    const initialBearing = initialViewport?.bearing ?? (viewMode === '3d' ? 20 : 0);

    // At most one hover card is ever shown. Each card positions itself via its own MapLibre
    // <Popup>; the only shared decision left is which one wins when several features are hovered
    // at once, resolved by precedence (POI/air-valve → fitting → pipeline). Gate conditions
    // mirror the render gates below.
    // Suppressed over the selected marker of EITHER family — a hover card floating on top
    // of the enlarged selected pin (while its details panel is open) is pure noise.
    const showPoiCard = Boolean(
        hoveredPoi && hoveredPoiLngLat && hoveredPoi.id !== selectedPoi?.id && hoveredPoi.id !== selectedLeak?.id,
    );
    // One cluster card for two sources of hover: the viewport clusters (Find & Fix, and the
    // explorer from the POI band inward) and the explorer bubbles outside it. Never both.
    const clusterCard =
        hoveredEntitySummary && hoveredClusterLngLat
            ? {
                  summary: hoveredEntitySummary,
                  lngLat: hoveredClusterLngLat,
                  onEnter: onClusterCardEnter,
                  onLeave: onClusterCardLeave,
              }
            : explorerBubbles.hoveredSummary && explorerBubbles.hoveredLngLat
              ? {
                    summary: explorerBubbles.hoveredSummary,
                    lngLat: explorerBubbles.hoveredLngLat,
                    onEnter: explorerBubbles.onCardEnter,
                    onLeave: explorerBubbles.onCardLeave,
                }
              : null;
    const showPoiClusterCard = clusterCard !== null;
    const showPipelineCard = Boolean(hoveredPipeline && hoveredPipelineLngLat);
    const showFittingCard = Boolean(hoveredFitting && hoveredFittingLngLat);
    const showAirValveCard = Boolean(isSavi && activeValve && activeCoords);
    const showVanCard = Boolean(hoveredVan && hoveredVanLngLat);

    const activeCards: HoverCardType[] = [];
    if (showPoiCard) activeCards.push('poi');
    if (showAirValveCard) activeCards.push('airvalve');
    if (showPoiClusterCard) activeCards.push('poiCluster');
    if (showVanCard) activeCards.push('van');
    if (showFittingCard) activeCards.push('fitting');
    if (showPipelineCard) activeCards.push('pipeline');
    // While the radial tool owns the map no hover card renders at all — the pipeline and
    // fitting hover hooks stay live (they are hover-only, with no click behaviour to
    // suppress), but their cards must not pop over the measurement.
    const winner = isRadialToolActive ? null : pickHoverCard(activeCards);

    return (
        <div className="flex h-full min-h-0 w-full overflow-hidden">
            {/* Content column: the Global Stats bar spans the full map-and-table workspace and
                stays visible in split and maximised table modes. Every right-hand panel (this
                component's own siblings + the route-level Stats config panel) runs full-height
                beside the whole column. */}
            <div className="flex min-w-0 flex-1 flex-col">
                {!isSavi && statsBarSlot}
                {/* Map-and-table workspace: map and Network Overview table share this row 50/50
                    in split mode; the map column is hidden when the table is maximised. */}
                <div className="flex min-h-0 flex-1">
                    {/* Desktop hides the map column outright while the table is maximised. Mobile
                        keeps it in flow at flex-1 instead: the maximised table is w-full shrink-0,
                        so this column is squeezed to zero width — which is what lets the table's
                        open/close width transition slide over a still-visible map rather than
                        popping in against an already-empty row. overflow-hidden is what makes the
                        squeeze complete: the map's absolutely-positioned overlays (the semantic
                        zoom stepper, the control stack) would otherwise paint out of the zero-width
                        column and float over the open table. */}
                    <div
                        className={cn(
                            'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
                            isTableMaximised && isDesktop && 'hidden',
                        )}
                    >
                        <div
                            ref={containerRef}
                            className={cn(
                                'relative min-h-0 min-w-0 flex-1',
                                viewMode === 'globe' && 'globe-space-background',
                            )}
                        >
                            <Map
                                ref={mapRef}
                                initialViewState={{
                                    longitude: initialViewport?.longitude ?? fallbackView.longitude,
                                    latitude: initialViewport?.latitude ?? fallbackView.latitude,
                                    zoom: initialViewport?.zoom ?? fallbackView.zoom,
                                    pitch: initialPitch,
                                    bearing: initialBearing,
                                }}
                                mapStyle={activeBasemap.style}
                                style={{ width: '100%', height: '100%' }}
                                minZoom={0}
                                maxPitch={85}
                                attributionControl={false}
                                onLoad={() => {
                                    setIsMapLoaded(true);
                                    setMapError(null);
                                }}
                                onError={(e) => {
                                    const err = e.error as (Error & { sourceId?: string }) | undefined;
                                    // Log the full error (message + stack) so staging-only failures that
                                    // surface with minified names (e.g. "DE is not defined") are decodable
                                    // against the emitted source maps, instead of an opaque object.
                                    console.error(
                                        '[MapLibre] error event:',
                                        err?.message ?? err,
                                        '\n',
                                        err?.stack ?? '',
                                        e,
                                    );
                                    // Only treat a failure as fatal (blank the map) when the map has not yet
                                    // loaded — i.e. the style/worker never came up. Transient source/tile/worker
                                    // errors after a successful load must not nuke the whole map.
                                    if (!isMapLoaded) {
                                        setMapError(MAP_ERROR_MESSAGE);
                                        showMapLoadErrorToast();
                                    }
                                }}
                                onMoveEnd={() => {
                                    const map = mapRef.current;
                                    if (!map) return;
                                    const zoom = map.getZoom();
                                    const viewport: MapViewport = {
                                        longitude: map.getCenter().lng,
                                        latitude: map.getCenter().lat,
                                        zoom,
                                        bearing: map.getBearing(),
                                        pitch: map.getPitch(),
                                    };
                                    currentViewportRef.current = viewport;
                                    if (!isExplorer) {
                                        saveMapPreferences({
                                            selectedBasemapId,
                                            viewMode,
                                            featureOverlays,
                                            poiTypes,
                                            viewport,
                                        });
                                    }
                                    syncViewportToUrl(viewport);
                                    // React bails out of re-renders when setState is called with the same
                                    // value, so panning without a semantic level change causes no extra render.
                                    setSemanticZoomLevel(mapLibreZoomToSemanticZoomLevel(zoom));
                                    setMapZoom(zoom);
                                }}
                            >
                                <AttributionControl position="bottom-left" compact={true} />

                                {/* Hover cards are positioned by MapLibre's own <Popup>, so they must live
                        inside <Map>. Precedence still decides which single card wins. */}
                                {winner === 'fitting' && hoveredFitting && hoveredFittingLngLat && (
                                    <FittingPopup
                                        fitting={hoveredFitting}
                                        longitude={hoveredFittingLngLat[0]}
                                        latitude={hoveredFittingLngLat[1]}
                                    />
                                )}

                                {winner === 'van' && hoveredVan && hoveredVanLngLat && (
                                    <VanPopup
                                        van={hoveredVan}
                                        longitude={hoveredVanLngLat[0]}
                                        latitude={hoveredVanLngLat[1]}
                                    />
                                )}

                                {winner === 'pipeline' && hoveredPipeline && hoveredPipelineLngLat && (
                                    <PipelinePopup
                                        pipeline={hoveredPipeline}
                                        longitude={hoveredPipelineLngLat[0]}
                                        latitude={hoveredPipelineLngLat[1]}
                                    />
                                )}

                                {winner === 'poi' && hoveredPoi && hoveredPoiLngLat && (
                                    <PoiPopup
                                        poi={hoveredPoi}
                                        longitude={hoveredPoiLngLat[0]}
                                        latitude={hoveredPoiLngLat[1]}
                                        onMouseEnter={onHoverCardEnter}
                                        onMouseLeave={onHoverCardLeave}
                                    />
                                )}

                                {winner === 'poiCluster' && clusterCard && (
                                    <PoiClusterPopup
                                        entitySummary={clusterCard.summary}
                                        longitude={clusterCard.lngLat[0]}
                                        latitude={clusterCard.lngLat[1]}
                                        onMouseEnter={clusterCard.onEnter}
                                        onMouseLeave={clusterCard.onLeave}
                                        // In the explorer a bubble, or a viewport cluster, may
                                        // span several clients' records.
                                        subtitle={isExplorer ? EXPLORER_CLUSTER_SUBTITLE : undefined}
                                    />
                                )}

                                {winner === 'airvalve' && activeValve && activeCoords && (
                                    <AirValvePopup
                                        valve={activeValve}
                                        highRiskDetail={activeHighRiskDetail}
                                        coords={activeCoords}
                                        onMouseEnter={onAirValveCardEnter}
                                        onMouseLeave={onAirValveCardLeave}
                                    />
                                )}

                                {/* The radial tool's live distance label, anchored to the
                                    measurement centre by MapLibre's own <Popup>. */}
                                {isRadialToolActive &&
                                    radialMeasurement.phase !== 'awaiting-centre' &&
                                    radialMeasurement.centre && (
                                        <RadialDistanceLabel
                                            centre={radialMeasurement.centre}
                                            radiusMeters={radialMeasurement.radiusMeters}
                                        />
                                    )}
                            </Map>

                            {!isMapLoaded && !mapError && (
                                <div className="bg-muted/60 absolute inset-0 flex items-center justify-center">
                                    <span className="text-muted-foreground text-sm">Loading map…</span>
                                </div>
                            )}

                            {mapError && (
                                <div className="bg-muted/60 absolute inset-0 flex items-center justify-center">
                                    <p className="text-destructive max-w-xs text-center text-sm">{mapError}</p>
                                </div>
                            )}

                            <SemanticZoomControl
                                mode={mode}
                                currentLevel={semanticZoomLevel}
                                zoom={mapZoom}
                                onChange={handleSemanticZoomChange}
                            />

                            <MapControls
                                mapRef={mapRef}
                                mode={mode}
                                isLayersPanelOpen={isLayersPanelOpen}
                                onToggleLayersPanel={handleToggleLayersPanel}
                                isFiltersPanelOpen={isFiltersPanelOpen}
                                onToggleFiltersPanel={handleToggleFiltersPanel}
                                isDownloadPanelOpen={isDownloadPanelOpen}
                                onToggleDownloadPanel={handleToggleDownloadPanel}
                                isMapKeyPanelOpen={isMapKeyPanelOpen}
                                onToggleMapKeyPanel={handleToggleMapKeyPanel}
                                viewMode={viewMode}
                                onViewModeChange={handleViewModeChange}
                                onPoiSelect={handleSelectPoi}
                                onDmaSelect={handleSearchDmaSelect}
                                onSearchCleared={handleSearchCleared}
                                isTableViewOpen={tableView.isOpen}
                                onToggleTableView={handleToggleTableView}
                                onLocateNetwork={handleResetToNetwork}
                                isRadialToolActive={isRadialToolActive}
                                onToggleRadialTool={handleToggleRadialTool}
                            />

                            {/* Click-selected AOI/DMA stats card, pinned to the map's top-left corner.
                    Independent of the hover-card precedence and the side panels — only one is
                    ever shown. Rendered outside <Map> now that it is a plain positioned overlay
                    rather than a feature-anchored MapLibre <Popup>. */}
                            {selection && (
                                <MapSelectionStatsCard
                                    selection={selection}
                                    stats={selectionStats.stats}
                                    isLoading={selectionStats.isLoading}
                                    isError={selectionStats.isError}
                                    onClose={handleCloseSelectionCard}
                                    onView={handleViewSelection}
                                    showViewButton={showSelectionViewButton}
                                />
                            )}

                            {isSavi && (
                                <SaviMapKeyCard
                                    isOpen={isMapKeyPanelOpen}
                                    onClose={handleCloseMapKeyPanel}
                                    highRiskCount={highRiskCount}
                                />
                            )}

                            <MapActivityBorder visible={showMapActivity} />
                        </div>
                    </div>
                    {entityMode && (
                        <NetworkTableRegion
                            layout={tableLayout}
                            clientId={selectedClient?.id ?? null}
                            // Committed (not draft) entity filters, and only once applied — the same
                            // state the map layers and stats consume, so every surface shows one
                            // filtered set.
                            filters={entityFilters.isActive ? entityFilters.committed : null}
                            actions={tableActions}
                            onMaximise={tableView.maximise}
                            onRestoreSplit={tableView.restoreSplit}
                            onClose={handleCloseTableView}
                            // The same toggle the map's Filters control uses, draft handling included.
                            onToggleFilters={handleToggleFiltersPanel}
                        />
                    )}
                </div>
            </div>

            <MapLayersPanel
                isOpen={isLayersPanelOpen}
                mode={mode}
                basemaps={basemaps}
                selectedBasemapId={selectedBasemapId}
                onBasemapChange={handleBasemapChange}
                onClose={() => {
                    setActivePanel(null);
                }}
                featureOverlays={featureOverlays}
                onFeatureOverlayChange={handleFeatureOverlayChange}
                poiTypes={poiTypes}
                onPoiTypeChange={handlePoiTypeChange}
                entityToggles={isSavi ? undefined : entityToggles}
                onEntityToggleChange={(key, enabled) => {
                    setEntityToggles((current) => ({ ...current, [key]: enabled }));
                }}
                highRiskCount={highRiskCount}
                canViewVehicleTracking={canViewVehicleTracking && !isSavi}
            />

            {/* SAVI keeps its own High Risk download; a SAVI filters panel does not exist
                (the Filters control is hidden there). */}
            {entityMode && (
                <EntityFiltersPanel
                    isOpen={isFiltersPanelOpen}
                    draft={entityFilters.draft}
                    onDraftChange={entityFilters.setDraft}
                    onApply={entityFilters.applyFilters}
                    onReset={entityFilters.resetFilters}
                    onClose={handleCloseFiltersPanel}
                    isActive={entityFilters.isActive}
                />
            )}

            {isSavi && <DownloadHighRiskPanel isOpen={isDownloadPanelOpen} onClose={handleCloseDownloadPanel} />}
            {entityMode && (
                <DownloadPanel
                    isOpen={isDownloadPanelOpen}
                    onClose={handleCloseDownloadPanel}
                    seedStatuses={
                        entityFilters.isActive
                            ? {
                                  poiStatuses: entityFilters.committed.poiStatuses,
                                  leakStatuses: entityFilters.committed.leakStatuses,
                              }
                            : null
                    }
                />
            )}

            {(entityMode || isExplorer) && (
                <LeakDetailsPanel
                    leak={selectedLeak}
                    clientId={selectedEntityClientId}
                    isOpen={selectedLeak !== null}
                    onClose={handleClearSelection}
                    onOpenInFindAndFix={
                        isExplorer && selectedLeak
                            ? () => {
                                  handleOpenInFindAndFix(selectedLeak, selectedLeakCoords);
                              }
                            : undefined
                    }
                />
            )}

            <PoiDetailsPanel
                poi={selectedPoi}
                poiCoords={selectedPoiCoords}
                clientId={selectedEntityClientId}
                isOpen={isPoiDetailsPanelOpen}
                onClose={handleClearSelection}
                onNext={navigateNext}
                onPrevious={navigatePrevious}
                canNavigate={canNavigate}
                hideNavigation={isExplorer}
                onOpenInFindAndFix={
                    isExplorer && selectedPoi
                        ? () => {
                              handleOpenInFindAndFix(selectedPoi, selectedPoiCoords);
                          }
                        : undefined
                }
                pipelines={poiPipelines}
            />

            <VanDetailsPanel
                van={selectedVan}
                isOpen={selectedVan !== null}
                onClose={handleClearSelection}
                onForbidden={revokeVehicleTracking}
                onShowTripTrack={setDisplayedTripTrack}
            />
        </div>
    );
}
